/* SPF INFINI layout runtime.
 * The page markup is a frozen copy of the Cargo site (declarative shadow DOM). Cargo sized it
 * with script; this recomputes the same viewport-dependent values so the layout matches at any
 * window size: base text size, mobile mode, page padding, column widths, pinned header/footer
 * offsets and background-video cover sizing.
 */
(function () {
  // Shopify serves its homepage at /shop, so the Cargo-era /shop -> /#shop URL redirect never
  // fires there; send the visitor to the shop section like the other section paths.
  if (/^\/shop\/?$/.test(location.pathname)) { location.replace('/#shop'); return; }
  var de = document.documentElement;
  var portraitMQ = matchMedia('(max-aspect-ratio: 4/5)');
  var landscapeMQ = matchMedia('(min-aspect-ratio: 2/3) and (max-height: 500px)');
  var lvhProbe;

  function px(n) { return n + 'px'; }
  // Cargo's layout unit (gutters, padding): the base size on desktop, base size x a fixed mobile
  // factor on mobile (data-mobile-unit on <html>, measured at build time). Not the root font size.
  function remPx() {
    var b = parseFloat(de.style.getPropertyValue('--base-size'));
    return isMobile() ? b * (parseFloat(de.getAttribute('data-mobile-unit')) || 1) : b;
  }
  function isMobile() { return portraitMQ.matches || landscapeMQ.matches; }

  // Cargo: base unit = min(width, height) * weight / 100, weight 9 (18 in phone landscape),
  // plus up to 5 more as the window gets taller than wide; never under 20. --base-size = unit * 0.16.
  function baseSize(w, h, mobile) {
    var tall = 0;
    if (h > w) tall = Math.min((h / w - 1) / 0.777777778, 1);
    var weight = (mobile && !portraitMQ.matches ? 18 : 9) + 5 * tall;
    return Math.max(Math.min(w, h) * weight / 100, 20) * 0.16;
  }

  function columnCss(set, mobile) {
    var g = parseFloat(set.getAttribute('gutter') || '1') * remPx();
    var units = [].filter.call(set.children, function (c) { return c.tagName === 'COLUMN-UNIT'; });
    var W = set.getBoundingClientRect().width;
    var stack = mobile && set.getAttribute('mobile-stack') !== 'false';
    if (stack) {
      // Stacked: full-width columns, empty spacer columns hidden, gutter below each visible one but the last.
      var visible = units.filter(function (u) { return !isEmpty(u); });
      var css = ':host{--columns-gutter:' + px(g) + ';margin-top:calc(var(--gutter-expand,0)*' + px(g) + ');' +
        'align-items:start;flex-direction:column;height:auto}' +
        '::slotted(column-unit:not(:last-child)){margin-right:0}' +
        '::slotted(column-unit){flex-basis:auto;--column-width:' + px(W) + ';--resize-parent-width:' + px(W) + '}';
      // Per-slot rules: the frozen desktop rules are per slot too, and would win on specificity.
      units.forEach(function (u) {
        var sel = '::slotted(column-unit[slot="' + u.getAttribute('slot') + '"])';
        var rule = '--column-width:' + px(W) + ';--resize-parent-width:' + px(W) + ';';
        if (isEmpty(u)) rule += 'display:none;';
        else rule += 'display:block;margin-bottom:' + (u !== visible[visible.length - 1] ? px(g) : '0') + ';';
        css += sel + '{' + rule + '}';
      });
      return css;
    }
    // Row: 12-column grid in fractional pixels (exact gutter gf); each column ends at the rounded
    // grid boundary and starts after the previous column plus the rounded gutter; the last takes
    // the rest. (Matches Cargo's widths, e.g. 251/250/250.97 for three columns at 770px.)
    var gf = g;
    g = Math.round(gf);
    var unit = (W - 11 * gf) / 12, cum = 0, x = 0;
    var out = ':host{--columns-gutter:' + px(g) + ';margin-top:calc(var(--gutter-expand,0)*' + px(g) + ');' +
      'align-items:stretch;flex-direction:row;height:auto}' +
      '::slotted(column-unit:not(:last-child)){margin-right:' + px(g) + '}' +
      '::slotted(column-unit){flex-basis:var(--column-width);margin-bottom:0;display:block}';
    units.forEach(function (u, i) {
      // No span attribute: Cargo splits the 12 columns equally.
      var span = parseFloat(u.getAttribute('span') || String(12 / units.length));
      cum += span;
      var cw = i === units.length - 1 ? W - x : Math.round(cum * unit + (cum - 1) * gf) - x;
      x += cw + g;
      out += '::slotted(column-unit[slot="' + u.getAttribute('slot') + '"]){--column-width:' + px(cw) + ';--resize-parent-width:' + px(cw) + '}';
    });
    return out;
  }

  // Cargo hides a stacked column only when it holds nothing at all (a lone <br> still counts).
  function isEmpty(u) {
    return !u.children.length && !u.textContent.replace(/\u00a0/g, ' ').trim();
  }

  function overrideStyle(host, css) {
    var root = host.shadowRoot;
    if (!root) return;
    var el = root.querySelector('style[data-spf]');
    if (!el) { el = document.createElement('style'); el.setAttribute('data-spf', ''); root.appendChild(el); }
    if (el.textContent !== css) el.textContent = css;
  }

  function coverVideos() {
    [].forEach.call(document.querySelectorAll('.video_wrapper_outer'), function (outer) {
      var box = outer.closest('.backdrop-contents') || outer.parentElement;
      var page = outer.closest('.page');
      var r = page.getBoundingClientRect();
      var w = r.width, h = Math.min(r.height, page.classList.contains('stacked-page') && box.classList.contains('inside') ? innerHeight : r.height);
      var v = outer.querySelector('video');
      var ratio = v && v.videoWidth ? v.videoWidth / v.videoHeight : 16 / 9;
      var iw = w, ih = w / ratio;
      if (ih < h) { ih = h; iw = h * ratio; }
      box.style.setProperty('--backdrop-width', px(w));
      box.style.setProperty('--backdrop-height', px(h));
      outer.style.width = px(w); outer.style.height = px(h);
      var inner = outer.querySelector('.video_wrapper_inner');
      if (inner) { inner.style.width = px(iw); inner.style.height = px(ih); }
      if (v) { v.style.width = px(iw); v.style.height = px(ih); }
    });
  }

  // Cargo requests the smallest standard width that covers the displayed width (x2 on retina),
  // capped at the original. data-src / data-natural-width are written at build time; data-src is
  // either a URL with a {w} slot or a Shopify CDN URL that takes ?width=.
  var BUCKETS = [100, 200, 250, 300, 400, 500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 4000, 5000];
  function sizeImages() {
    var dpr = (window.devicePixelRatio || 1) >= 1.2 ? 2 : 1;
    [].forEach.call(document.querySelectorAll('media-item[data-src]'), function (mi) {
      var img = mi.shadowRoot && mi.shadowRoot.querySelector('img[part~="media"]');
      if (!img) return;
      var base = mi.getAttribute('data-src'), src = base;
      // SVGs on Shopify's CDN are served as-is (no resizing); Cargo URLs always carry a size.
      if (base.indexOf('{w}') >= 0 || !/\.svg(\?|$)/i.test(base)) {
        var natural = parseFloat(mi.getAttribute('data-natural-width')) || Infinity;
        var want = mi.getBoundingClientRect().width * dpr, w = BUCKETS[BUCKETS.length - 1];
        for (var i = 0; i < BUCKETS.length; i++) if (BUCKETS[i] >= want) { w = BUCKETS[i]; break; }
        w = Math.min(w, natural);
        src = base.indexOf('{w}') >= 0 ? base.replace('{w}', w) : base + (base.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
      }
      if (img.getAttribute('src') !== src) img.setAttribute('src', src);
    });
  }

  // Cargo's fit-text (uses="fit-text"): the text's scale is set in its shadow style as
  // `:host { --font-scale: X !important }` so one line exactly fills its container's width.
  function fitText() {
    [].forEach.call(document.querySelectorAll('[uses~="fit-text"]'), function (el) {
      var box = el.parentElement;
      if (!box || !el.shadowRoot) return;
      var cs = getComputedStyle(box);
      var avail = box.getBoundingClientRect().width - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      var scale = parseFloat(getComputedStyle(el).getPropertyValue('--font-scale')) || 1;
      for (var i = 0; i < 3; i++) {
        var w = el.getBoundingClientRect().width;
        if (!w || !avail) break;
        scale = scale * avail / w;
        overrideStyle(el, ':host{--font-scale:' + scale + '!important}');
      }
    });
  }

  // Cargo's marquee-set: one line of content scrolling sideways, a clone right after it for the
  // loop. Height follows the content; speed is `speed` x 6 px/s (negative = leftward), as measured
  // on the Cargo site.
  var marqueeState = [];
  function marquees() {
    marqueeState = [];
    [].forEach.call(document.querySelectorAll('marquee-set'), function (m) {
      var inner = m.querySelector('marquee-inner[slot="contents"]');
      var mc = m.shadowRoot && m.shadowRoot.querySelector('.marquee-contents');
      if (!inner || !mc || !inner.textContent.trim() && !inner.children.length) return;
      var W = m.getBoundingClientRect().width;
      overrideStyle(m, ':host{--resize-parent-width:' + px(W) + '}::slotted(marquee-inner){min-width:' + px(W) + '}');
      var r = inner.getBoundingClientRect();
      overrideStyle(m, ':host{height:' + px(r.height) + ';--resize-parent-width:' + px(W) + '}::slotted(marquee-inner){min-width:' + px(W) + '}.marquee-positioner.clone{left:' + px(r.width) + '}');
      var t = (mc.style.transform.match(/translate\(([-0-9.]+)px/) || [0, 0])[1];
      marqueeState.push({ mc: mc, period: r.width, speed: parseFloat(m.getAttribute('speed') || '0') * 6, pos: parseFloat(t) || 0 });
    });
  }
  var lastTick = 0;
  function tick(now) {
    var dt = lastTick ? Math.min(now - lastTick, 100) / 1000 : 0;
    lastTick = now;
    marqueeState.forEach(function (q) {
      if (!q.period) return;
      q.pos += q.speed * dt;
      while (q.pos <= -q.period) q.pos += q.period;
      while (q.pos > 0) q.pos -= q.period;
      q.mc.style.transform = 'translate(' + Math.round(q.pos) + 'px, 0px)';
    });
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  function layout() {
    if (!lvhProbe) {
      lvhProbe = document.createElement('div');
      lvhProbe.style.cssText = 'position:fixed;top:0;height:100lvh;width:0;visibility:hidden;pointer-events:none';
      document.body.appendChild(lvhProbe);
    }
    var w = de.clientWidth, h = innerHeight, mobile = isMobile();
    de.classList.toggle('mobile', mobile);
    document.body.classList.toggle('mobile', mobile);
    de.style.setProperty('--base-size', px(baseSize(w, Math.max(h, lvhProbe.getBoundingClientRect().height), mobile)));
    ['--viewport-height', '--min-viewport-height', '--visible-viewport-height'].forEach(function (k) { de.style.setProperty(k, px(h)); });
    var rem = remPx();

    // Page padding is set in rem per page (data-pad, written at build time).
    [].forEach.call(document.querySelectorAll('.page-content[data-pad]'), function (pc) {
      var p = parseFloat(pc.getAttribute('data-pad')) * rem;
      pc.style.setProperty('--page-content-pad-top', px(p));
      pc.style.setProperty('--page-content-pad-bottom', px(p));
    });

    // Pinned header: on mobile the first stacked page starts below it; on desktop it overlays.
    var header = document.querySelector('.page.pinned-top:not([hidden])' + (mobile ? '[data-mode="mobile"]' : '[data-mode="desktop"]'));
    var first = document.querySelector('.page.stacked-page');
    var top = mobile && header ? header.getBoundingClientRect().height : 0;
    if (first) { first.style.setProperty('--pin-padding-top', px(top)); first.style.paddingTop = top ? px(top) : ''; }
    // Pinned footer overlays the bottom of the last stacked page.
    var footer = document.querySelector('.page.pinned-bottom');
    var stacked = document.querySelectorAll('.page.stacked-page'), last = stacked[stacked.length - 1];
    if (footer && last) {
      var fh = footer.getBoundingClientRect().height;
      last.style.setProperty('--pin-padding-bottom', px(fh));
      last.style.paddingBottom = px(fh);
    }

    [].forEach.call(document.querySelectorAll('column-set'), function (set) { overrideStyle(set, columnCss(set, mobile)); });

    [].forEach.call(document.querySelectorAll('bodycopy'), function (bc) {
      var page = bc.closest('.page'), pc = bc.closest('.page-content');
      var cs = pc ? getComputedStyle(pc) : null;
      var pad = cs ? parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) : 0;
      var pins = page ? (parseFloat(page.style.getPropertyValue('--pin-padding-top')) || 0) + (parseFloat(page.style.getPropertyValue('--pin-padding-bottom')) || 0) : 0;
      bc.style.setProperty('--resize-parent-width', px(bc.getBoundingClientRect().width));
      bc.style.setProperty('--fit-height', px(h - pad - pins));
    });
    // Column widths depend on bodycopy widths and vice versa only through padding; one more pass settles nested sets.
    [].forEach.call(document.querySelectorAll('column-set'), function (set) { overrideStyle(set, columnCss(set, mobile)); });
    fitText();
    marquees();
    coverVideos();
    sizeImages();
    // The shop grid replaces a Buy Button iframe, whose inner width was rounded to whole pixels.
    // Browsers also place iframes on whole pixels; snap the grid the same way (text rendering).
    [].forEach.call(document.querySelectorAll('spf-shop'), function (el) {
      el.style.width = ''; el.style.left = ''; el.style.top = '';
      var r = el.getBoundingClientRect(), x = r.left + scrollX, y = r.top + scrollY;
      el.style.position = 'relative';
      el.style.width = px(Math.round(r.width));
      el.style.left = px(Math.round(x) - x);
      el.style.top = px(Math.round(y) - y);
    });
  }

  var raf;
  function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(layout); }
  layout();
  addEventListener('resize', schedule);
  portraitMQ.addEventListener('change', schedule);
  landscapeMQ.addEventListener('change', schedule);
  document.fonts && document.fonts.ready.then(schedule);
  addEventListener('load', schedule);
  [].forEach.call(document.querySelectorAll('video'), function (v) { v.addEventListener('loadedmetadata', schedule); });

  // Mobile menu: the header's #overlay link opens Cargo's overlay page (frozen in the header
  // snippet, hidden until opened); any link inside it, a tap outside its links, or Escape closes it.
  var menu = document.querySelector('[data-spf-menu]');
  function setMenu(open) {
    if (!menu) return;
    menu.hidden = !open;
    menu.classList.toggle('overlay-open', open);
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (a && a.getAttribute('href') === '#overlay') { e.preventDefault(); setMenu(menu && menu.hidden); return; }
    if (menu && !menu.hidden && (a || !e.target.closest('[data-spf-menu] bodycopy a'))) setMenu(false);
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setMenu(false); });
})();
